import loadable from '@loadable/component';
import algoliasearch from 'algoliasearch/lite';
import classNames from 'classnames';
import { bool, func, number, object, oneOf, shape, string } from 'prop-types';
import qs from 'qs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  Hits,
  HitsPerPage,
  InstantSearch,
  Pagination as InstantSearchPagination,
  SearchBox,
  useInstantSearch,
  usePagination,
  useSearchBox,
  useSortBy,
  useStats,
} from 'react-instantsearch';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import aa from 'search-insights';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import {
  BUSINESS_PROFILE_LISTING_TYPE,
  DEAL_LISTING_TYPE,
  PRODUCT_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE,
  oneSignalMerchantNotificationTemplets,
  propTypes,
} from '../../util/types';
import { withViewport } from '../../util/uiHelpers';
import { encodeLatLngBounds, parse } from '../../util/urlHelpers';

import {
  BrandingIconCard,
  Button,
  CurrencySymbol,
  H3,
  LocationAutocompleteInput,
  Modal,
  Page,
} from '../../components';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import AlgoliaSearchDealCard from '../AlogoliaSearchCard/AlgoliaSearchDealCard';
import AlgoliaSearchServiceCard from '../AlogoliaSearchCard/AlgoliaSearchServiceCard';
import CategoryStrip from '../../components/CategoryStrip/CategoryStrip';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  categoriesListing,
  getAlgoliasearchResults,
  serviceCategoriesListing,
} from './AlgoliaSearchPage.duck';
import { createSearchResultSchema, searchParamsPicker } from './AlgoliaSearchPage.helpers';
import {
  buildListingSearchFilters,
  buildLocationSearchQuery,
  buildPublishedFiltersString,
  LOCATION_SEARCH_RADIUS_METERS,
} from './AlgoliaSearchPage.filters';
import {
  isAlgoliaLocationDebugEnabled,
  logAlgoliaLocationDebug,
  logAlgoliaSearchClientError,
  logAlgoliaSearchClientRequest,
  logAlgoliaSearchClientResponse,
  logAlgoliaLocationSnapshot,
  printAlgoliaLocationDebugHint,
  registerAlgoliaLocationDebugGlobals,
} from './AlgoliaSearchPage.locationDebug';

import { onGetAdsByListingType, onGetObjectsFromAlgolia, onSendNotification, onSendPushNotification } from '../../util/api';
import {
  categoryDisplayName,
  convertCategoriesList,
  findDbCategoryByRef,
  transformDbCategories,
} from '../../util/data';
import css from './AlgoliaSearchPage.module.css';
import Filters from './Filters';
import ListingCategoryTypeFilter from './ListingCategoryTypeFilter';
import mixpanel from 'mixpanel-browser';
import { mixPanelEventsConstants, mixPanelEventsScreens } from '../../mixpanel';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';

const Autocomplete = loadable(() =>
  import(/* webpackChunkName: "Autocomplete" */ './AlgoliaAutocomplete')
);

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY
);

/* Debug logging wrapper — does not mutate requests (Configure owns query/geo). */
const createDebugSearchClient = baseClient => ({
  ...baseClient,
  search(requests) {
    const requestId = logAlgoliaSearchClientRequest({
      requests,
      locationContext: {},
      phase: 'passthrough',
    });
    return baseClient.search(requests).then(
      response => {
        logAlgoliaSearchClientResponse(requestId, response);
        return response;
      },
      error => {
        logAlgoliaSearchClientError(requestId, error);
        throw error;
      }
    );
  },
});

const searchClientWithDebug = createDebugSearchClient(searchClient);

const FILTER_DEBOUNCE_MS = 400;

/** Debounce a value; downstream filter work is derived via useMemo, not in the timer. */
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

aa('init', {
  appId: process.env.REACT_APP_ALGOLIA_APP_ID,
  apiKey: process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY,
});

const ALGOLIA_INSIGHTS_ANON_KEY = 'algolia_insights_anonymous_id';

const getAlgoliaInsightsUserToken = currentUser => {
  const userId = currentUser?.id?.uuid;
  if (userId) {
    return userId;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  let anonymousId = window.localStorage.getItem(ALGOLIA_INSIGHTS_ANON_KEY);
  if (!anonymousId) {
    anonymousId = `anonymous-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    window.localStorage.setItem(ALGOLIA_INSIGHTS_ANON_KEY, anonymousId);
  }
  return anonymousId;
};

const searchStateToUrl = searchState =>
  searchState ? `${window.location.pathname}?${qs.stringify(searchState)}` : '';

const Panel = ({ children, header, footer }) => {
  return (
    <div className="ais-Panel">
      {header && <div className="ais-Panel-header">{header}</div>}
      <div className="ais-Panel-body">{children}</div>
      {footer && <div className="ais-Panel-footer">{footer}</div>}
    </div>
  );
};

const CustomStats = props => {
  const { nbHits, page, hitsPerPage, nbPages } = useStats();
  const { refinementList, productDev, intl } = props;
  return (
    <div className={css.noDataFoundWrapper}>
      <span className={css.searchResults}>
        {/* Showing {page + 1} - {hitsPerPage} out of {nbHits} products */}
        {nbHits} {intl.formatMessage({ id: 'ViewBusinessPage.results' })}
      </span>
      <div
        className={classNames(
          css.noDataFound,
          refinementList || productDev ? null : css.noDataFoundleft
        )}
      >
        {' '}
        {nbHits < 1 ? intl.formatMessage({ id: 'ViewBusinessPage.noDataFound' }) : null}
      </div>
    </div>
  );
};

const CustomPageStat = ({ listingType }) => {
  const { nbHits, page, hitsPerPage, nbPages } = useStats();

  const pageClickHandler = e => {
    if (typeof window != undefined) {
      if (e.target.textContent == '›' && listingType) {
        window.history.pushState('', '', `/s/${listingType}?page=${page + 2}`);
        return;
      } else if (e.target.textContent == '‹' && page != 0 && listingType) {
        window.history.pushState('', '', `/s/${listingType}?page=${page}`);
        return;
      } else if (/^\d$/.test(e.target.textContent) && listingType) {
        window.history.pushState('', '', `/s/${listingType}?page=${e.target.textContent}`);
      } else {
        e.preventDefault();
      }
    }
  };

  return (
    <InstantSearchPagination
      onClick={e => {
        if (nbPages > 1) {
          pageClickHandler(e);
        }
      }}
    />
  );
};

// Determine the Algolia index based on the pathname
const determineAlgoliaIndex = pathname => {
  switch (pathname) {
    case 'deals':
      return process.env.REACT_APP_ALGOLIA_DEALS_INDEX;
    case 'services':
      return process.env.REACT_APP_ALGOLIA_SERVICE_INDEX;
    case 'staffs':
      return process.env.REACT_APP_ALGOLIA_STAFF_INDEX;
    case 'products':
      return process.env.REACT_APP_ALGOLIA_PRODUCTS_INDEX;
    default:
      return process.env.REACT_APP_ALGOLIA_DEALS_INDEX;
  }
};

const getListingTypeFromParam = {
  services: SERVICE_LISTING_TYPE,
  products: PRODUCT_LISTING_TYPE,
  deals: DEAL_LISTING_TYPE,
};

const NoResultsSuggestion = ({ suggestion, intl, categories }) => {
  if (!suggestion?.fromCategoryId || !suggestion?.toCategoryId) return null;
  // `categories` is the nested tree; flatten so a subcategory resolves too.
  // Both ids reaching here may be a DB slug (label comes from the row) or a
  // legacy numeric id (label comes from the category.* translations) —
  // categoryDisplayName picks the right one. Previously this always took the
  // translation path and rendered "category.9f8722b8-…" for a slug.
  const flatCategories = (categories || []).flatMap(parent => [
    parent,
    ...(parent.childCategory || []),
  ]);
  const labelById = id =>
    categoryDisplayName({
      intl,
      ref: id,
      resolved: findDbCategoryByRef(flatCategories, id),
      legacyDisplayId: id,
    });
  return (
    <div className={css.noResultsSuggestion}>
      <FormattedMessage
        id="LatestServices.noListingsSuggestion"
        values={{
          from: labelById(suggestion.fromCategoryId),
          to: labelById(suggestion.toCategoryId),
          cat: chunks => <span className={css.suggestionCategory}>{chunks}</span>,
        }}
      />
    </div>
  );
};

const ServicesNoResultsHandler = ({
  listingType,
  selectedCategory,
  effectiveCategory,
  setEffectiveCategory,
  setFacetFilterOverride,
  suggestion,
  setSuggestion,
  categories,
  filterParams,
  aroundLatLang,
}) => {
  const { nbHits } = useStats();
  const lastSuggestedForCategoryRef = useRef('');
  const fallbackInFlightRef = useRef(false);

  useEffect(() => {
    if (listingType !== 'services') return;
    if (!selectedCategory) {
      lastSuggestedForCategoryRef.current = '';
      if (suggestion) setSuggestion(null);
      return;
    }
    if (effectiveCategory !== selectedCategory) return;
    if (typeof nbHits !== 'number') return;
    if (nbHits > 0) {
      // Keep the suggestion visible while we're showing fallback results.
      if (suggestion && effectiveCategory === selectedCategory) setSuggestion(null);
      return;
    }

    if (fallbackInFlightRef.current) return;
    if (lastSuggestedForCategoryRef.current === selectedCategory) return;

    fallbackInFlightRef.current = true;
    lastSuggestedForCategoryRef.current = selectedCategory;

    (async () => {
      try {
        const indexName = process.env.REACT_APP_ALGOLIA_SERVICE_INDEX;
        if (!indexName) return;

        const filters =
          (filterParams?.length ? `state:published AND ${filterParams.join(' AND ')}` : 'state:published') ||
          'state:published';
        const response = await searchClient.search([
          {
            indexName,
            params: {
              hitsPerPage: 0,
              facets: ['publicData.categoryLevel2'],
              maxValuesPerFacet: 50,
              ...(aroundLatLang
                ? {
                    aroundLatLng: aroundLatLang,
                    aroundRadius: LOCATION_SEARCH_RADIUS_METERS,
                  }
                : {}),
              ...(filters ? { filters } : {}),
            },
          },
        ]);

        const facetCounts = response?.results?.[0]?.facets?.['publicData.categoryLevel2'] || {};
        const orderedCategoryIds = Array.isArray(categories)
          ? categories.map(c => String(c?.id)).filter(Boolean)
          : [];
        const selectedIndex = orderedCategoryIds.indexOf(String(selectedCategory));
        const neighborIds =
          selectedIndex >= 0
            ? [
                orderedCategoryIds[selectedIndex - 1],
                orderedCategoryIds[selectedIndex + 1],
                orderedCategoryIds[selectedIndex - 2],
                orderedCategoryIds[selectedIndex + 2],
                orderedCategoryIds[selectedIndex - 3],
                orderedCategoryIds[selectedIndex + 3],
              ].filter(Boolean)
            : [];

        const bestNeighbor = neighborIds.find(
          id => id && id !== selectedCategory && Number(facetCounts?.[id] || 0) > 0
        );
        const fallbackCategoryId =
          bestNeighbor ||
          Object.entries(facetCounts)
            .filter(([id, count]) => id && id !== selectedCategory && Number(count) > 0)
            .sort((a, b) => b[1] - a[1])?.[0]?.[0] ||
          null;

        if (!fallbackCategoryId) return;

        setEffectiveCategory(fallbackCategoryId);
        setFacetFilterOverride([`publicData.categoryLevel2:${fallbackCategoryId}`]);
        setSuggestion({ fromCategoryId: selectedCategory, toCategoryId: fallbackCategoryId });
      } catch (e) {
        // silent
      } finally {
        fallbackInFlightRef.current = false;
      }
    })();
  }, [
    aroundLatLang,
    categories,
    effectiveCategory,
    filterParams,
    listingType,
    nbHits,
    selectedCategory,
    setEffectiveCategory,
    setFacetFilterOverride,
    setSuggestion,
    suggestion,
  ]);

  return null;
};

function SortBy(props) {
  const { currentRefinement, options, refine } = useSortBy(props);
  const [selectedOption, setSelectedOption] = useState(options[0].value); // Set initial selected option to the first option
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Update currentRefinement with selectedOption on component mount
    refine(selectedOption);
  }, []); // Empty dependency array ensures this effect runs only once, on mount

  useEffect(() => {
    // Close dropdown when clicking outside
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSortChange = value => {
    setSelectedOption(value);
    refine(value);
    setIsOpen(false); // Close dropdown after selecting an option
  };

  return (
    <div className={css.custom_dropdown} ref={dropdownRef}>
      <div className={css.dropdown_header} onClick={handleToggleDropdown}>
        {options.find(option => option.value === currentRefinement)?.label || 'Select'}
      </div>
      {isOpen && (
        <ul className={css.dropdown_options}>
          {options.map(option => (
            <li key={option.value}>
              <Button
                className={classNames(
                  currentRefinement === option.value ? css.active : '',
                  css.optionButton
                )}
                onClick={() => handleSortChange(option.value)}
              >
                {option.label}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Forwards Algolia Configure props (filters, facets, geo, merged query).
 */
const ListingSearchConfigure = ({
  filterParams,
  facetFilters,
  aroundLatLang,
  keywordQuery,
}) => {
  const filters = useMemo(() => buildPublishedFiltersString(filterParams), [filterParams]);

  const { query } = useMemo(
    () => buildLocationSearchQuery({ keywordQuery }),
    [keywordQuery]
  );

  const configureProps = {
    ruleContexts: [],
    filters,
    query,
    aroundLatLng: aroundLatLang?.length > 0 ? aroundLatLang : undefined,
    aroundRadius:
      aroundLatLang?.length > 0 ? LOCATION_SEARCH_RADIUS_METERS : undefined,
  };

  if (facetFilters?.length) {
    configureProps.facetFilters = facetFilters;
  }

  useEffect(() => {
    logAlgoliaLocationDebug('configure:props', {
      filters,
      facetFilters: facetFilters || [],
      query,
      aroundLatLng: configureProps.aroundLatLng,
      aroundRadius: configureProps.aroundRadius,
      keywordQuery,
    });
  }, [aroundLatLang, facetFilters, filters, keywordQuery, query]);

  return <Configure {...configureProps} />;
};

/**
 * Logs InstantSearch UI stats vs filter state (paste snapshot when reporting bugs).
 */
const LocationSearchDebugWatcher = ({
  locationSearch,
  aroundLatLang,
  filterParams,
  facetFilters,
  keywordQuery,
  indexName,
}) => {
  const { nbHits, nbPages, page, processingTimeMS, query: statsQuery } = useStats();
  const { query: searchBoxQuery } = useSearchBox();
  const { status, error } = useInstantSearch();

  const effectiveQuery = useMemo(
    () => buildLocationSearchQuery({ keywordQuery }),
    [keywordQuery]
  );

  useEffect(() => {
    printAlgoliaLocationDebugHint();
    registerAlgoliaLocationDebugGlobals();
  }, []);

  useEffect(() => {
    if (!isAlgoliaLocationDebugEnabled()) {
      return undefined;
    }
    logAlgoliaLocationSnapshot({
      indexName,
      instantSearchStatus: status,
      instantSearchError: error?.message || null,
      ui: {
        nbHits,
        nbPages,
        page,
        processingTimeMS,
        statsQuery,
        searchBoxQuery,
      },
      filters: {
        keywordQuery,
        effectiveQuery: effectiveQuery.query,
        filterParams,
        facetFilters,
        maxPriceFilter: filterParams?.find(p => p.includes('price.amount')),
      },
      location: {
        locationSearch,
        aroundLatLang,
        aroundRadius: aroundLatLang ? LOCATION_SEARCH_RADIUS_METERS : undefined,
      },
    });
    return undefined;
  }, [
    aroundLatLang,
    error,
    facetFilters,
    filterParams,
    indexName,
    locationSearch,
    nbHits,
    nbPages,
    page,
    processingTimeMS,
    searchBoxQuery,
    statsQuery,
    status,
    effectiveQuery,
    keywordQuery,
  ]);

  return null;
};

const getCenterFromBounds = bounds => {
  if (bounds?.ne && bounds?.sw) {
    const centerLatitude = (bounds.ne.lat + bounds.sw.lat) / 2;
    const centerLongitude = (bounds.ne.lng + bounds.sw.lng) / 2;
    return { lat: centerLatitude, lng: centerLongitude };
  }
  return null;
};

const buildLocationValueFromUrl = (searchParams, queryParams) => {
  const address = searchParams?.address || '';
  const bounds = queryParams?.bounds;
  if (!address && !bounds) {
    return { search: '', selectedPlace: null };
  }
  return {
    search: address,
    selectedPlace: bounds ? { address, bounds } : null,
  };
};

const ListingLocationFilter = ({ intl, locationValue, onLocationChange }) => {
  const noop = () => {};

  return (
    <LocationAutocompleteInput
      className={css.filterLocationRoot}
      inputClassName={css.filterLocationInput}
      predictionsClassName={css.filterLocationPredictions}
      iconClassName={css.filterLocationIcon}
      placeholder={intl.formatMessage({ id: 'AlgoliaSearchPage.locationPlaceholder' })}
      closeOnBlur
      input={{
        name: 'location',
        value: locationValue,
        onChange: onLocationChange,
        onFocus: noop,
        onBlur: noop,
      }}
      meta={{ valid: true, touched: false }}
    />
  );
};

const InstantSearchUrlSync = ({ page }) => {
  const { refine: refinePage } = usePagination();
  const syncedRef = useRef(false);

  useEffect(() => {
    if (syncedRef.current) {
      return undefined;
    }
    syncedRef.current = true;
    if (page > 1) {
      refinePage(page - 1);
    }
    return undefined;
  }, [page, refinePage]);

  return null;
};

const AlgoliaSearchPageComponent = props => {
  const {
    intl,
    config,
    location,
    listings,
    currentUser,
    scrollingDisabled,
    routeConfiguration,
    onUpdateProfile,
    onManageDisableScrolling,
    history,
    params,
  } = props;

  // Parse search query only once
  const searchParams = useMemo(() => parse(location.search), [location.search]);
  const queryParams = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const [locationValue, setLocationValue] = useState(() =>
    buildLocationValueFromUrl(searchParams, queryParams)
  );
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const selectedCategory = selectedType !== 'all' ? selectedType : null;
  const [userLocation, setUserLocation] = useState({});
  const [allListings, setAllListings] = useState([]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        setUserLocation({
          lat: parseFloat(latitude.toFixed(3)),
          lng: parseFloat(longitude.toFixed(3)),
        });
      },
      () => {
        // Distance chip is hidden if permission is denied.
      }
    );
  }, []);
  const { listingType } = params;

  const handleLocationChange = value => {
    setLocationValue(value);
    const { selectedPlace, search } = value || {};
    const params = parse(location.search);

    if (selectedPlace?.bounds) {
      params.address = search || '';
      params.bounds = encodeLatLngBounds(selectedPlace.bounds);
      const query = qs.stringify(params);
      history.replace(query ? `${window.location.pathname}?${query}` : window.location.pathname);
      logAlgoliaLocationDebug('location:place-selected', {
        address: search,
        bounds: params.bounds,
        aroundRadius: LOCATION_SEARCH_RADIUS_METERS,
      });
      return;
    }

    if (!search) {
      delete params.address;
      delete params.bounds;
      const query = qs.stringify(params);
      history.replace(query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }
  };

  const handleTypeChange = event => {
    const nextType = event.target.value;
    setSelectedType(nextType);
    if (listingType === 'deals' || listingType === 'services') {
      const nextParams = { ...searchParams };
      if (nextType === 'all') {
        delete nextParams['publicData.categoryLevel2'];
        delete nextParams['publicData.listingsCategories'];
        if (listingType === 'services') {
          delete nextParams['publicData.categoryLevel1'];
        }
      } else if (listingType === 'deals') {
        delete nextParams['publicData.categoryLevel2'];
        nextParams['publicData.listingsCategories'] = nextType;
      } else if (parentCategoryIds.has(nextType)) {
        // Parent category selected → filter by categoryLevel1 (all listings under the parent).
        delete nextParams['publicData.listingsCategories'];
        delete nextParams['publicData.categoryLevel2'];
        nextParams['publicData.categoryLevel1'] = nextType;
      } else {
        delete nextParams['publicData.listingsCategories'];
        nextParams['publicData.categoryLevel2'] = nextType;
        delete nextParams['publicData.categoryLevel1'];
      }
      const query = qs.stringify(nextParams);
      history.replace(query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }
  };
  const notDealTypes = [
    BUSINESS_PROFILE_LISTING_TYPE,
    PRODUCT_LISTING_TYPE,
    SERVICE_LISTING_TYPE,
    DEAL_LISTING_TYPE,
    STAFF_LISTING_TYPE,
  ];
  const listingTypes = config.listing.listingTypes;
  const filteredListingTypes = listingTypes.filter(
    type => !notDealTypes.includes(type?.listingType)
  );
  // Extract keywords from search params
  const initialSearchState = searchParams.keywords || '';
  const initialPage = searchParams.page ? parseInt(searchParams.page, 10) : 0;
  const [searchState, setSearchState] = useState({
    query: initialSearchState,
    page: initialPage,
    menu: {
      ...searchParams,
    },
  });
  const [keywordQuery, setKeywordQuery] = useState(initialSearchState);

  // Determine the appropriate Algolia index
  const roleName = determineAlgoliaIndex(listingType);
  const [isMobileFilter, setIsMobileFilter] = useState(false);
  const [isMoreFilters, setIsMoreFilters] = useState(false);
  const [dealCat, setDealCat] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facetFilterOverride, setFacetFilterOverride] = useState(null);
  const [effectiveCategory, setEffectiveCategory] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const debouncedMaxPrice = useDebouncedValue(maxPrice, FILTER_DEBOUNCE_MS);

  useEffect(() => {
    logAlgoliaLocationDebug('input:location-changed', { locationValue });
  }, [locationValue]);

  useEffect(() => {
    const token = getAlgoliaInsightsUserToken(currentUser);
    if (token) {
      aa('setUserToken', token);
    }
  }, [currentUser?.id?.uuid]);

  /**
   * The option's value is what gets written into the search params and then
   * sent to Algolia as a facet filter, so it has to equal what the listings
   * actually store.
   *
   * Both modes store the category *slug*: services in
   * publicData.categoryLevel1/2, deals in listingsCategories (inherited from
   * the linked service listing). Using the row uuid here matched nothing and
   * every category reported 0 listings.
   */
  const categoryValueOf = useCallback(category => String(category.slug || category.id), []);

  // For deals, listingsCategories stores subcategory ids, so a selected *parent* is matched by
  // OR-ing its subcategories (built below) rather than a single (non-matching) filter.
  const isDealParentSelected =
    listingType === 'deals' &&
    selectedType !== 'all' &&
    (categories || []).some(parent => categoryValueOf(parent) === String(selectedType));

  const { filterParams, facetFilters: baseFacetFilters } = useMemo(
    () =>
      buildListingSearchFilters({
        listingType,
        maxPrice: debouncedMaxPrice,
        // Parent deals handled via the OR filter below, so skip the single (wrong) category filter.
        selectedType: isDealParentSelected ? 'all' : selectedType,
      }),
    [listingType, debouncedMaxPrice, selectedType, isDealParentSelected]
  );

  // Home-page parent categories deep-link with publicData.categoryLevel1 to show every listing
  // across the parent's subcategories. The services index facets categoryLevel1, and selecting a
  // subcategory removes this param (see handleTypeChange), so this only applies while no sub is picked.
  const parentCategoryLevel1 =
    listingType === 'services' ? searchParams?.['publicData.categoryLevel1'] : null;
  const facetFilters =
    facetFilterOverride ??
    (parentCategoryLevel1
      ? [`publicData.categoryLevel1:${parentCategoryLevel1}`]
      : baseFacetFilters);

  // Deals: a selected parent → OR over its subcategory ids on the listingsCategories array attr.
  const dealParentFilterString = useMemo(() => {
    if (!isDealParentSelected) return null;
    // listingsCategories only ever holds *subcategory* values, so a selected
    // parent has to be expanded into an OR over its children — filtering on
    // the parent's own value matches nothing.
    //
    // Children come from the categories tree rather than
    // getSubcategoryIdsForParent, which resolves against the legacy numeric
    // taxonomy and returns nothing for a slug or uuid parent.
    const parent = (categories || []).find(p => categoryValueOf(p) === String(selectedType));
    const subIds = (parent?.childCategory || []).map(categoryValueOf);
    if (!subIds.length) return null;
    return `(${subIds.map(id => `publicData.listingsCategories:${id}`).join(' OR ')})`;
  }, [isDealParentSelected, selectedType, categories, categoryValueOf]);

  const effectiveFilterParams = dealParentFilterString
    ? [...(filterParams || []), dealParentFilterString]
    : filterParams;

  const serviceSubcategories = useMemo(
    () =>
      (categories || []).flatMap(parent =>
        (parent.childCategory || []).map(child => ({
          id: categoryValueOf(child),
          label: child.label,
          parentId: categoryValueOf(parent),
        }))
      ),
    [categories, categoryValueOf]
  );

  // Dropdown options: each parent (selectable → filters by categoryLevel1) followed by its
  // subcategories (selectable → filters by categoryLevel2).
  const categoryOptions = useMemo(
    () =>
      (categories || []).flatMap(parent => [
        { id: categoryValueOf(parent), label: parent.label, isParent: true },
        ...(parent.childCategory || []).map(child => ({
          id: categoryValueOf(child),
          label: child.label,
          isParent: false,
          parentId: categoryValueOf(parent),
        })),
      ]),
    [categories, categoryValueOf]
  );

  const parentCategoryIds = useMemo(
    () => new Set((categories || []).map(parent => categoryValueOf(parent))),
    [categories, categoryValueOf]
  );

  const activeBounds = useMemo(() => {
    if (locationValue?.selectedPlace?.bounds) {
      return locationValue.selectedPlace.bounds;
    }
    return parse(location.search, { latlngBounds: ['bounds'] })?.bounds || null;
  }, [location.search, locationValue?.selectedPlace?.bounds]);

  const aroundLatLang = useMemo(() => {
    const center = getCenterFromBounds(activeBounds);
    return center ? `${center.lat},${center.lng}` : '';
  }, [activeBounds]);

  const categoryStripFacetContext = useMemo(() => {
    const { filterParams: stripFilterParams } = buildListingSearchFilters({
      listingType,
      maxPrice: debouncedMaxPrice,
      selectedType: 'all',
    });
    return {
      filterParams: stripFilterParams,
      aroundLatLng: aroundLatLang?.length > 0 ? aroundLatLang : undefined,
      aroundRadius:
        aroundLatLang?.length > 0 ? LOCATION_SEARCH_RADIUS_METERS : undefined,
    };
  }, [aroundLatLang, debouncedMaxPrice, listingType]);

  const dispatch = useDispatch();
  const updatedDealCategories = convertCategoriesList(dealCat);

  function generateUniqueId(length = 10) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let uniqueId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      uniqueId += characters[randomIndex];
    }
    return uniqueId;
  }

  if (typeof window === 'undefined') return null;
  useEffect(() => {
    const dealCategories = async () => {
      try {
        const res = await dispatch(categoriesListing());
        setDealCat(res);
      } catch (error) {
        console.error('Failed to fetch top categories:', error);
      }
    };
    const categoriesList = async () => {
      try {
        const data = await dispatch(serviceCategoriesListing());
        setCategories(transformDbCategories(data));
      } catch (error) {
        console.error('Failed to fetch top categories:', error);
      }
    };
    categoriesList();
    dealCategories();
    const handlePopState = ({ state }) => {
      setSearchState({ ...searchState, state });
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const onSearchStateChange = (searchState, updateAfter = 500) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(searchState, null, searchStateToUrl(searchState));
    }
    setSearchState(previousState => {
      const hasQueryChanged = previousState?.searchState?.query !== searchState?.query;

      return {
        ...previousState,
        searchState: {
          ...searchState,
          boundingBox: !hasQueryChanged ? searchState.boundingBox : null,
        },
      };
    });
  };

  useEffect(() => {
    if (listingType === 'deals') {
      const listingsCategories = searchParams?.['publicData.listingsCategories'];
      const legacyCategoryLevel2 = searchParams?.['publicData.categoryLevel2'];
      const categoryFromUrl = listingsCategories || legacyCategoryLevel2;
      if (categoryFromUrl) {
        setSelectedType(String(categoryFromUrl));
      }
      return;
    }
    if (listingType === 'services') {
      const categoryLevel2 = searchParams?.['publicData.categoryLevel2'];
      const categoryLevel1 = searchParams?.['publicData.categoryLevel1'];
      if (categoryLevel2) {
        setSelectedType(String(categoryLevel2));
      } else if (categoryLevel1) {
        // Parent-category deep-link from the home page: reflect the parent as the selected value
        // so the filter dropdown shows it (e.g. "Home Services") instead of "All types".
        setSelectedType(String(categoryLevel1));
      }
    }
  }, [listingType, searchParams]);

  useEffect(() => {
    const urlSearchParams = parse(location.search);
    // Reflect a category deep-link (home-page parent card or a shared URL) as the selected type so
    // the dropdown shows it on initial load instead of resetting to "All types".
    const initialSelectedType =
      listingType === 'services'
        ? urlSearchParams['publicData.categoryLevel2'] ||
          urlSearchParams['publicData.categoryLevel1'] ||
          'all'
        : listingType === 'deals'
          ? urlSearchParams['publicData.listingsCategories'] ||
            urlSearchParams['publicData.categoryLevel2'] ||
            'all'
          : 'all';

    // Honor a keyword deep-link (e.g. the home hero "Find Deals" search) instead of clearing it.
    const urlKeyword = urlSearchParams.keywords || '';
    setMaxPrice('');
    setSelectedType(String(initialSelectedType));
    setFacetFilterOverride(null);
    setKeywordQuery(urlKeyword);
    setSearchState(prev => ({ ...prev, query: urlKeyword }));
    const urlQueryParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    setLocationValue(buildLocationValueFromUrl(urlSearchParams, urlQueryParams));
    const getAdsByListingType = async () => {
      const ads = await onGetAdsByListingType({ channel: 'Web', placement: 'Product Listing Pages', listingType: listingType.slice(0, -1) });
      if (ads.length > 0) {
        const adListingIds = ads.map(ad => ad.listingid);
        const allListings = await onGetObjectsFromAlgolia({ adListingIds, listingType: listingType.slice(0, -1) });
        const shuffledAds = [...allListings?.results].sort(() => Math.random() - 0.5);
        const top5Ads = shuffledAds.slice(0, 3);
        const promotedAds = top5Ads.map(ad => {
          const originalAd = ads.find(a => a.listingid === ad.objectID);
          return { ...ad, isPromoted: true, adId: originalAd ? originalAd.id : undefined };
        });

        setAllListings(promotedAds);
      }
    }
    // getAdsByListingType();
    if (currentUser) {
      if (listingType == 'products') {
        // trackEventsMixpanel(mixPanelEventsConstants.PRODUCTS, {
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        //   action_type: mixPanelEventsConstants.VIEW_PRODUCTS,
        // });
        // trackEventsMixpanel(mixPanelEventsConstants.NAV_BAR, {
        //   action_type: mixPanelEventsConstants.VIEW_PRODUCTS,
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        // });
      }
      if (listingType == 'services') {
        // trackEventsMixpanel(mixPanelEventsConstants.SERVICES, {
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        //   action_type: mixPanelEventsConstants.VIEW_SERVICES_ALL,
        // });
        // trackEventsMixpanel(mixPanelEventsConstants.NAV_BAR, {
        //   action_type: mixPanelEventsConstants.VIEW_SERVICES,
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        // });
      }
      if (listingType == 'deals') {
        // trackEventsMixpanel(mixPanelEventsConstants.DEALS, {
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        //   action_type: mixPanelEventsConstants.VIEW_DEALS,
        // });
        // trackEventsMixpanel(mixPanelEventsConstants.NAV_BAR, {
        //   action_type: mixPanelEventsConstants.VIEW_DEALS,
        //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
        //   user_type: 'Customer',
        //   user_id: currentUser?.id.uuid,
        // });
      }
    }
  }, [listingType]);

  useEffect(() => {
    const urlSearchParams = parse(location.search);
    const urlQueryParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    setLocationValue(buildLocationValueFromUrl(urlSearchParams, urlQueryParams));
  }, [location.search]);
  /**
   * Toggles a listing's favorited status for the current user.
   *
   * @param {string} listingId - The ID of the listing to be toggled.
   * @param {string} type - The type of the listing (either 'deal' or 'service').
   * @param {object} user - The current user object containing profile data.
   */
  const toggleFavorite = (listingId, type, user, authorId, title, images) => {
    // Determine the correct wishlist key based on the type of listing
    const wishlistKey =
      type === SERVICE_LISTING_TYPE
        ? 'wishlist_service_ids'
        : type === DEAL_LISTING_TYPE
          ? 'wishlist_deal_ids'
          : type === PRODUCT_LISTING_TYPE
            ? 'wishlist_product_ids'
            : null;

    const userWishlist = user?.attributes?.profile?.publicData?.[wishlistKey] || [];
    // Check if the listing ID is currently in the wishlist
    const isInWishlist = userWishlist.includes(listingId);

    // Create a new wishlist array based on whether the listing ID is already present
    const updatedWishlist = isInWishlist
      ? userWishlist.filter(id => id !== listingId) // Remove the listing ID if it's already in the wishlist
      : [...userWishlist, listingId]; // Add the listing ID if it's not in the wishlist

    // Prepare the updated profile data
    const profileUpdate = {
      publicData: {
        [wishlistKey]: updatedWishlist, // Set the updated wishlist in the profile data
      },
    };

    const firstImage = Array.isArray(images) && images?.length ? images?.[0] : null;

    if (!isInWishlist) {
      const type = getListingTypeFromParam?.[listingType];
      const entityName = type === 'deal' ? 'Deal' : type === 'product' ? 'Product' : 'Service';

      const customData = {
        image: firstImage ? firstImage : '',
        entityName: entityName,
        senderName: currentUser?.attributes?.profile?.displayName,
        senderId: user?.id?.uuid,
        actionId: listingId,
      };
      const likeParams = {
        userRole: 'customer',
        receiverUserIds: [authorId],
        senderUserId: user?.id?.uuid,
        template_id: oneSignalMerchantNotificationTemplets?.LIKE,
        notificationData: customData,
        customData: customData,
      };
      onSendPushNotification(likeParams).then(() => { });
    }

    // Update the user's profile with the new wishlist data
    onUpdateProfile(profileUpdate)
      .then(response => console.log('Profile updated successfully:', response))
      .catch(error => console.error('Failed to update profile:', error));
  };

  const Hit = props => {
    const { hit } = props;
    switch (roleName) {
      case process.env.REACT_APP_ALGOLIA_DEALS_INDEX:
        return (
          <AlgoliaSearchDealCard
            hit={hit}
            roleName={roleName}
            currentUser={currentUser}
            toggleFavorite={toggleFavorite}
            updatedDealCategories={updatedDealCategories}
            history={history}
            routeConfiguration={routeConfiguration}
            userLocation={userLocation}
          />
        );
      case process.env.REACT_APP_ALGOLIA_SERVICE_INDEX:
        return (
          <AlgoliaSearchServiceCard
            hit={hit}
            roleName={roleName}
            currentUser={currentUser}
            toggleFavorite={toggleFavorite}
            history={history}
            routeConfiguration={routeConfiguration}
          />
        );
      case process.env.REACT_APP_ALGOLIA_PRODUCTS_INDEX:
        return (
          <AlgoliaSearchServiceCard
            hit={hit}
            roleName={roleName}
            currentUser={currentUser}
            toggleFavorite={toggleFavorite}
            history={history}
            routeConfiguration={routeConfiguration}
          />
        );
      default:
        return (
          <AlgoliaSearchDealCard
            hit={hit}
            roleName={roleName}
            currentUser={currentUser}
            toggleFavorite={toggleFavorite}
            history={history}
            routeConfiguration={routeConfiguration}
            userLocation={userLocation}
          />
        );
    }
  };

  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

  const topbarAlgoliComponent = (
    <div className={classNames(css.searchBoxTopbar, css.searchBox)}>
      <Autocomplete
        key={roleName}
        searchClient={searchClient}
        placeholder={intl.formatMessage({
          id: `AlgoliaSearchPage.searchPlaceholder.${listingType}`,
        })}
        detachedMediaQuery="none"
        searchState={searchState}
        indexName={roleName}
        currentUser={currentUser}
        onKeywordQueryChange={setKeywordQuery}
        openOnFocus
      />
    </div>
  );

  const filterSearchComponent = (
    <div className={css.filterSearchWrap}>
      <span className={css.filterSearchIcon} aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" fill="none" />
          <path d="m21 21-4.3-4.3" fill="none" />
        </svg>
      </span>
      <Autocomplete
        key={roleName}
        className={css.filterSearchBox}
        searchClient={searchClient}
        placeholder={intl.formatMessage({
          id: `AlgoliaSearchPage.searchPlaceholder.${listingType}`,
        })}
        detachedMediaQuery="none"
        searchState={searchState}
        indexName={roleName}
        currentUser={currentUser}
        onKeywordQueryChange={setKeywordQuery}
        openOnFocus
      />
    </div>
  );

  const { searchParamsInURL } = searchParamsPicker(
    location.search,
    searchParams,
    listingFieldsConfig,
    defaultFiltersConfig,
    sortConfig,
    false
  );

  const isWindowDefined = typeof window !== 'undefined';
  if (!isWindowDefined) return null;

  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    intl,
    routeConfiguration,
    config
  );

  useEffect(() => {
    setFacetFilterOverride(null);
  }, [selectedType, debouncedMaxPrice, listingType]);

  useEffect(() => {
    if (selectedType && selectedType !== 'all' && listingType === 'services') {
      setEffectiveCategory(String(selectedType));
      setSuggestion(null);
    } else if (listingType === 'services') {
      setEffectiveCategory('');
      setSuggestion(null);
    }
  }, [selectedType, listingType]);

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
      className={classNames(
        css.mainWrapper,
        (listingType === 'services' || listingType === 'deals') && css.mainWrapperLovable
      )}
    >
      {location.pathname == '/s/products' ? (

         <div className={css.messagePanel}>
          <TopbarContainer
                currentPage="AlgoliaSearchPage"
                currentSearchParams={{
                  searchState,
                  onSearchStateChange: onSearchStateChange,
                }}
                topbarAlgoliComponent={topbarAlgoliComponent}
              />
         <H3 as="h2" className={css.heading}>
           <FormattedMessage id="Coming Soon..."/>
         </H3>
       </div>
  
      ) : (
        <>
        <div
          className={classNames(
            css.container,
            (listingType === 'services' || listingType === 'deals') && css.containerLovable
          )}
        >
        <div
          className={classNames(
            css.searchWrapper,
            (listingType === 'services' || listingType === 'deals') && css.searchWrapperLovable
          )}
        >
          {/* <div className={css.categoryName}>
            <h3>{CategoryName}</h3>
          </div> */}
           {
         
          <InstantSearch
            key={roleName}
            future={{
              preserveSharedStateOnUnmount: true,
            }}
            searchClient={searchClientWithDebug}
            indexName={roleName}
            insights={true}
          >
            <div style={{ display: 'none' }} aria-hidden="true">
              <SearchBox />
            </div>
            <InstantSearchUrlSync
              page={searchState.page > 0 ? searchState.page : 1}
            />
            <TopbarContainer
                currentPage="AlgoliaSearchPage"
                currentSearchParams={{
                  searchState,
                  onSearchStateChange: onSearchStateChange,
                }}
                topbarAlgoliComponent={topbarAlgoliComponent}
              />
              <LocationSearchDebugWatcher
                indexName={roleName}
                locationSearch={locationValue?.search || ''}
                aroundLatLang={aroundLatLang}
                filterParams={effectiveFilterParams}
                facetFilters={facetFilters}
                keywordQuery={keywordQuery}
              />
              <ListingSearchConfigure
                filterParams={effectiveFilterParams}
                facetFilters={facetFilters}
                aroundLatLang={aroundLatLang}
                keywordQuery={keywordQuery}
              />
              <ServicesNoResultsHandler
                listingType={listingType}
                selectedCategory={
                  parentCategoryLevel1 || isDealParentSelected ? null : selectedCategory
                }
                effectiveCategory={effectiveCategory}
                setEffectiveCategory={setEffectiveCategory}
                setFacetFilterOverride={setFacetFilterOverride}
                suggestion={suggestion}
                setSuggestion={setSuggestion}
                categories={serviceSubcategories}
                filterParams={filterParams}
                aroundLatLang={aroundLatLang}
              />

              <div
                className={classNames(
                  css.searchContents,
                  (listingType === 'services' || listingType === 'deals') &&
                    css.searchContentsLovable,
                  arabicLanguage && css.arabicDirection
                )}
              >
                <div className={css.servicesList}>
                  <div className={css.servicesHeading}>
                    {intl.formatMessage({ id: `AlgoliaSearchPage.heading.${listingType}` })}
                  </div>
                  <div className={css.subTitle}>
                    {intl.formatMessage({ id: `AlgoliaSearchPage.subHeading.${listingType}` })}
                  </div>
                  <div className={css.searchFilterBar}>
                    <div className={css.filterGrid}>
                      <div className={classNames(css.filterCell, css.filterCellSearch)}>
                        {filterSearchComponent}
                      </div>

                      <div className={classNames(css.filterCell, css.filterCellLocation)}>
                        <ListingLocationFilter
                          intl={intl}
                          locationValue={locationValue}
                          onLocationChange={handleLocationChange}
                        />
                      </div>

                      <div className={classNames(css.filterCell, css.filterCellPrice)}>
                        <div className={css.filterFieldWithCurrency}>
                          <CurrencySymbol size={14} className={css.filterCurrencyIcon} />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className={css.filterField}
                            value={maxPrice}
                            onChange={e => setMaxPrice(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'AlgoliaSearchPage.maxPricePlaceholder',
                            })}
                            aria-label={intl.formatMessage({
                              id: 'AlgoliaSearchPage.maxPricePlaceholder',
                            })}
                          />
                        </div>
                      </div>

                      <div className={classNames(css.filterCell, css.filterCellType)}>
                        <ListingCategoryTypeFilter
                          value={selectedType}
                          onChange={handleTypeChange}
                          options={listingType === 'services' || listingType === 'deals' ? categoryOptions : serviceSubcategories}
                          intl={intl}
                          ariaLabel={intl.formatMessage({
                            id: 'AlgoliaSearchPage.typeFilterLabel',
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                  {listingType === 'services' || listingType === 'deals' ? (
                    <CategoryStrip
                      mode={listingType}
                      embedded
                      history={history}
                      routeConfiguration={routeConfiguration}
                      facetSearchContext={categoryStripFacetContext}
                      activeCategoryId={
                        listingType === 'deals'
                          ? searchParams?.['publicData.listingsCategories']
                          : searchParams?.['publicData.categoryLevel1']
                      }
                    />
                  ) : null}

                  {listingType === 'services' ? (
                    <NoResultsSuggestion
                      suggestion={suggestion}
                      intl={intl}
                      categories={serviceSubcategories}
                    />
                  ) : null}

                <section className="container-results">
                  <header className="container-header container-options header-desktop">
                    <div>
                      <CustomStats
                        refinementList={searchState?.refinementList}
                        productDev={searchState?.productDev?.refinementList}
                        intl={intl}
                      />
                    </div>
                    <HitsPerPage
                      className="container-option"
                      items={[
                        {
                          value: 12,
                          default: true,
                        },
                      ]}
                    />
                  </header>
                </section>

                <div
                  className={classNames(
                    css.aisInstantSearch,
                    currentUser ? '' : css.notLoginUserSearch
                  )}
                >
                  <Modal
                    isOpen={isMobileFilter}
                    onClose={() => setIsMobileFilter(!isMobileFilter)}
                    onManageDisableScrolling={onManageDisableScrolling}
                    modalTitle="Filters"
                    id="AlgoliasSearchPage.filterModal"
                    className={css.filterWrapper}
                  >
                    <div className={classNames(css.filterWrapper)}>
                      <div>
                        <div className={css.faqsec}>
                          <div className={css.faqwrapper}>
                            {roleName.includes('DEALS') && (
                              <Filters onSearchStateChange={onSearchStateChange} {...props} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Modal>
                  <div
                    className={classNames(
                      !searchState?.refinementList || !searchState?.productDev?.refinementList
                        ? css.smallMargin
                        : null,
                      css.Resultcontainer
                    )}
                  >
                    <div
                      className={classNames(
                        searchState?.refinementList || searchState?.productDev?.refinementList
                          ? !searchState?.refinementList
                            ? css.show5card
                            : null
                          : css.show5card
                      )}
                    >
                      {allListings?.length > 0 ? (
                        <div
                          className={
                            listingType === 'deals'
                              ? css.allListings
                              : listingType === 'services'
                              ? css.allServices
                              : css.allProducts
                          }
                        >
                          <div>
                            {allListings?.slice(0, 3).map(listing => {
                              if(listing.publicData.listingType === 'deal'){
                                return (
                                  <AlgoliaSearchDealCard
                                    // key={listing.id.uuid}
                                    hit={listing}
                                    roleName={roleName}
                                    currentUser={currentUser}
                                    toggleFavorite={toggleFavorite}
                                    updatedDealCategories={updatedDealCategories}
                                    history={history}
                                    routeConfiguration={routeConfiguration}
                                  />
  
                                );
                              }
                              else if(listing.publicData.listingType === 'service'){
                                return (
                                  <AlgoliaSearchServiceCard
                                    // key={listing.id.uuid}
                                    hit={listing}
                                    roleName={roleName}
                                    currentUser={currentUser}
                                    toggleFavorite={toggleFavorite}
                                    history={history}
                                    routeConfiguration={routeConfiguration}
                                  />
                                );
                              }
                              else if(listing.publicData.listingType === 'product'){
                                return (
                                  <AlgoliaSearchServiceCard
                                    // key={listing.id.uuid}
                                    hit={listing}
                                    roleName={roleName}
                                    currentUser={currentUser}
                                    toggleFavorite={toggleFavorite}
                                    history={history}
                                    routeConfiguration={routeConfiguration}

                                  />
                                );
                              }
                              return null; // Handle unknown listing types
                          
                          })}
                         </div>
                        </div>
                      ) : null}
                      <Hits
                        hitComponent={Hit}
                        className={
                          roleName.includes('SERVICES')
                            ? css.serviceCards
                            : roleName.includes('DEALS')
                              ? css.dealCards
                              : css.productCards
                        }
                      />
                      <footer className="container-footer">
                        <CustomPageStat listingType={listingType} />
                      </footer>
                    </div>
                  </div>
                </div>
              </div>
          </InstantSearch>}
        </div>
      </div>
        <div className={css.pageFooter}>
          <FooterContainer />
        </div>
        </>
      )}
    </Page>
  );
};

AlgoliaSearchPageComponent.defaultProps = {
  listings: [],
  mapListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  activeListingId: null,
  initialSearchFormValues: {},
};

AlgoliaSearchPageComponent.propTypes = {
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,
  initialSearchFormValues: object,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const EnhancedAlgoliaSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  return props.searchInProgress ? (
    <div className={css.loader_overlay}>
      <div className={css.loader}></div>
    </div>
  ) : (
    <AlgoliaSearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentUser,
    currentUserListing,
    currentUserListingFetched,
    categories,
    subCategories,
    subChildCategories,
  } = state.user;
  const { searchInProgress, algoliaSearchResults } = state.AlgoliaSearchPage || {};
  const { updateInProgress } = state.ProfileSettingsPage;
  // const dealsCategories = state.AlgoliaSearchPage.dealsCategories;
  // console.log('dealsCategories :>> ', dealsCategories);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    currentUserListing,
    currentUserListingFetched,
    categories,
    subCategories,
    subChildCategories,
    updateInProgress,
    algoliaSearchResults,
    searchInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onGetAlgoliasearchResults: params => dispatch(getAlgoliasearchResults(params)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const AlgoliaSearchPage = compose(
  withViewport,
  connect(mapStateToProps, mapDispatchToProps)
)(EnhancedAlgoliaSearchPage);

export default AlgoliaSearchPage;
